// Librairies
import { CommonModule } from '@angular/common';
import { Component, effect, inject, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { endOfDay, format, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ViewerModule } from "ng2-adsk-forge-viewer";
import { BehaviorSubject, Subscription, take } from 'rxjs';

// Services
import { PlanningService } from 'src/app/services/api/plannings/planning.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { PlanningStore } from 'src/app/services/stores/planning.store';

// Interfaces
import { Fragment } from 'src/app/interfaces/api/fragment';
import { Metric } from 'src/app/interfaces/api/metric';
import { Model } from 'src/app/interfaces/api/model';
import { Planning } from 'src/app/interfaces/api/planning';
import { PlanningTask } from 'src/app/interfaces/api/task';
import { User } from 'src/app/interfaces/api/user';

// Classes
import { defaultColors } from 'src/app/classes/widget/chartHandlerV2';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';
import * as threeDtools from '../../../../../classes/shared/threeDTools';
import { PointShape } from '../../../../../classes/viewer/shapes/point';

// Components
import { SpinnerComponent } from 'projects/ui-library/src/lib/components/spinner/spinner.component';
import { ZoneShape } from 'src/app/classes/viewer/shapes/zone';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { ViewerComponent } from 'src/app/components/project-pages/planning/viewer/viewer.component';

export interface PlanningDayData {
  date: string;
  name: string;
  metricCount: number;
  color?: string;
  volume: number;
  volumeText?: string;
  metrics: Metric[];
  fragments: string[]
  active?: string;
  concreting?: number;
  concretingVolume?: number;
  concretingVolumeText?: string;
  linear?: number;
  linearText?: string;
  element?: number;
  surface?: number;
  surfaceText?: string;
  craneCycle?: number;
  buildingTypeArray?: any[];
  buildingTypeValues?: any;
  buildingTypes?: any
}

const BUILDING_TYPE_CONFIG: Record<string, { unit: string; variable: string; icon: string; color: string }> = {
  // Murs
  "wall": { unit: "m", variable: "linearProgress", icon: "crop_16_9", color: "#FF6600" },
  "prefawall": { unit: "m", variable: "linearProgress", icon: "space_dashboard", color: "#FF8C33" },

  // Planchers/Dalles
  "floor": { unit: "m2", variable: "surfaceProgress", icon: "layers", color: "#CC0000" },
  "prefafloor": { unit: "m2", variable: "surfaceProgress", icon: "grid_3x3", color: "#FF3333" },

  // Poutres
  "beam": { unit: "", variable: "", icon: "horizontal_rule", color: "#f56892" },
  "prefabeam": { unit: "", variable: "", icon: "timeline", color: "#FA7FB8" },

  // Poteaux
  "column": { unit: "", variable: "", icon: "vertical_align_center", color: "#7B1FA2" },
  "prefacolumn": { unit: "", variable: "", icon: "architecture", color: "#9C27B0" },
};

@Component({
  selector: 'widget-planning',
  imports: [
    WidgetLoadingComponent,
    WidgetTitleComponent,
    CommonModule,
    SpinnerComponent,
    ViewerComponent,
    TranslateModule,
    ViewerModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule
  ],
  standalone: true,
  templateUrl: './planning.widget.html',
  styleUrl: './planning.widget.scss'
})

export class PlanningWidget extends WidgetDirective {
  // Services
  private readonly planningService: PlanningService = inject(PlanningService);
  private readonly planningStore: PlanningStore = inject(PlanningStore);
  private readonly authService: AuthService = inject(AuthService);

  // Component API
  @ViewChild('viewer', { static: false }) viewerComponent: any;

  // Signals
  model: WritableSignal<Model | null> = signal<Model | null>(null);
  currentDate: WritableSignal<string> = signal<string>("");
  currentDay: WritableSignal<string> = signal<string>("");
  selectedFragments: WritableSignal<Fragment[]> = signal<Fragment[]>([]);
  readonly currentUser: Signal<User> = this.authService.currentUser;
  readonly selectedDay: Signal<string> = this.planningStore.selectedDay;

  // Variables
  blockStyle = "width:100%;display:flex;flex-direction: column;flex:1;min-height:0;";
  titleStyle: string = "font-size:16px;margin:0px 20px 5px;";
  chartData: any;
  viewerOptions: any;
  viewer: any;
  currentTask: any;
  currentMetric: any;
  planningData$: BehaviorSubject<any> = new BehaviorSubject(null);
  tasks$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  metrics$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  fragments$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  currentTask$: BehaviorSubject<any> = new BehaviorSubject(null);
  currentMetric$: BehaviorSubject<any> = new BehaviorSubject(null);
  selectedMetrics$: BehaviorSubject<string[]> = new BehaviorSubject<any[]>([]);
  selectedTasks$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  startDate?: string;
  endDate?: string;
  planning?: Planning;
  metrics: Metric[] = [];
  tasks: PlanningTask[] = [];
  fragments: Fragment[] = [];
  planningLoading = false;
  days: PlanningDayData[] = [];
  chartRefresh?: Subscription;
  chartExport?: Subscription;
  widgetIsReady: boolean = false;
  chartRendered: boolean = false;
  theme: string = "light3";
  fragmentById: Record<string, Fragment> = {};
  loaded: boolean = false;
  selectedTaskIds: string[] = [];
  loadingTasks: boolean = false;
  allTasks: PlanningTask[] = [];
  tasksInitialized: boolean = false;
  tasksControl = new FormControl<string[]>([]);
  private allFragmentsCache: Fragment[] = [];

  constructor() {
    super();

    effect(() => {
      const selectedDayIds = new Set(
        this.selectedDay().split(',').filter(x => x.trim())
      );

      this.days.forEach(d => {
        const firstMetricId = d.metrics[0]?.id;
        d.active = firstMetricId && selectedDayIds.has(firstMetricId) ? "active" : "";
      });
    });
  }

  init() {
    if (!this.widget || !this.widget.visible || this.resize) return

    this.widgetDesign = this.widget.design;
    this.widgetData = this.widget.data;

    if (this.type !== this.widget.type) {
      this.resetStyles();
    }

    this.type = this.widget.type;
    this.initOptions();

    if (!this.widgetIsReady) {
      setTimeout(() => this.withContent(() => {
        this.initPlanning3D();
        this.updateForge();
        this.widgetRendered = true;
      }), 200)
    } else {
      this.updatePlanning3D();
      this.widgetRendered = true;
    }
  }

  resetStyles() {
    this.blockStyle = "width:100%;display:flex;flex-direction: column;align-content:center;";
    this.titleStyle = "font-size:16px;margin:0px 20px 20px;";
  }

  initOptions() {
    try {
      this.resetStyles();
      let options = this.widgetDesign;

      if (!options) return

      this.blockStyle += `height:100%;text-align:'start';`;

    } catch (e) {
      console.error(e)
    }
  }

  initPlanning3D(selectedTaskIds?: string[]) {
    if (!this.widget || !this.widget.design) return;

    this.loadingTasks = true;

    const planningId = this.widget.design.planning3D.planning;
    const defaultTaskIds = [...this.widget.design.planning3D.tasks];

    this.model.set(this.widget.content.model as Model);

    if (!planningId) return;

    if (!this.tasksInitialized) {
      this.selectedTaskIds = [...defaultTaskIds];
      this.tasksInitialized = true;
    }

    this.planningLoading = true;

    if (this.allTasks.length === 0) {
      this.planningService.getTasks({ planning: planningId }).pipe(take(1)).subscribe((tasks: PlanningTask[]) => {
        this.allTasks = tasks;
        const taskIds = selectedTaskIds ?? defaultTaskIds;
        this.loadPlanningData(planningId, taskIds);
      });

    } else {
      const taskIds = selectedTaskIds ?? this.selectedTaskIds;
      this.loadPlanningData(planningId, taskIds);
    }
  }

  loadPlanningData(planningId: string, taskIds: string[]) {
    this.tasks = this.allTasks.filter(t => taskIds.includes(t.id));
    this.planningStore.setTasks(this.tasks);
    this.tasksControl.setValue(taskIds);

    if (this.allFragmentsCache.length > 0) {
      // Use fragments data in cache
      this.fragments = this.allFragmentsCache.filter(f =>
        f.task && taskIds.includes(f.task)
      );
      this.fragments.forEach(f => {
        this.fragmentById[f.id] = f;
      });
      this.planningStore.setFragments(this.fragments);
      this.loadMetrics(planningId, taskIds);
    } else {
      // Load fragments data
      this.planningService.getFragments({ planning: planningId }).pipe(take(1)).subscribe((allFragments: Fragment[]) => {
        this.allFragmentsCache = allFragments;

        this.fragments = allFragments.filter(f =>
          f.task && taskIds.includes(f.task)
        );
        this.fragments.forEach(f => {
          this.fragmentById[f.id] = f;
        });

        this.planningStore.setFragments(this.fragments);
        this.loadMetrics(planningId, taskIds);
      });
    }
  }


  loadMetrics(planningId: string, taskIds: string[]) {
    const query = {
      planning: planningId,
      tasks: taskIds,
      startDate: this.startDate || "",
      endDate: this.endDate || "",
      prediction: []
    };

    this.planningService.getMetrics(query).pipe(take(1)).subscribe(metrics => {
      this.metrics = metrics;
      this.days = [];
      this.setDayData();
      this.planningLoading = false;
      this.planningStore.onDataLoaded();
      this.loadingTasks = false;
    });
  }

  updatePlanning3D() {
    let model = this.model()

    if (!model) return;

    this.onLoaded3D();

    if (this.widgetData)
      this.widgetData.forEach((d: any) => {
        if (d.x && d.y && d.x > -180 && d.x < 180) {
          if (Array.isArray(d.y))
            d.y = d.y[0];

          if (isNaN(d.y) || d.y < -90 || d.y > 90) return null;

          let { x, y, z } = threeDtools.applyTransform2({ lon: d.x, lat: d.y, z: d.z || model.origin.position.z }, model.origin, model.offset);
          let point: any = {};
          point.x = x;
          point.y = y;
          point.z = z;
          point.scale = model.origin.scale;
          this.setForgePoint(point);
        }

        return null;
      })
  }

  selectDay(day: PlanningDayData) {
    this.currentDay.set(day.date)

    this.days.forEach(d => {
      if (d.date != day.date)
        d.active = "";
      else
        d.active = "active";
    })

    this.planningStore.selectMetrics(day.metrics)
    let fragments = day.fragments.map(f => this.planningStore.fragmentByIds()[f]).filter(f => !!f)
    this.planningStore.selectFragments(fragments);
  }

  setDayData() {
    this.metrics.forEach(m => {
      let date = endOfDay(m.payload.startDate ?? new Date()).toISOString();
      let day = this.days.find(d => d.date == date);

      if (day) {
        m.payload.color = day.color;
        day.metrics.push(m);

        if (m.payload['fragment'])
          day.fragments.push(m.payload['fragment']);
      } else {
        const dayColor = defaultColors[this.days.length % 7];
        m.payload.color = dayColor;

        this.days.push({
          date,
          name: format(new Date(date), 'd MMMM', { locale: fr }),
          metricCount: 1,
          color: dayColor,
          volume: m.payload['volume'] ? parseFloat(m.payload['volume']) : 0,
          metrics: [m],
          fragments: m.payload['fragment'] ? [m.payload['fragment'] as string] : []
        })
      }
    })

    this.days.sort((a, b) => isBefore(a.date, b.date) ? 1 : -1);

    this.days.forEach(d => {
      d.volume = 0;
      d.concreting = 0;
      d.concretingVolume = 0;
      d.linear = 0;
      d.element = 0;
      d.surface = 0;
      d.craneCycle = 0;
      d.buildingTypeArray = [];
      d.buildingTypeValues = {};

      d.buildingTypes = Object.entries(BUILDING_TYPE_CONFIG).reduce((acc, [key, config]) => {
        acc[key] = { ...config, volume: 0, value: 0 };
        return acc;
      }, {} as Record<string, any>);

      d.metrics.forEach((m: Metric) => {
        let buildingTypeName = m.payload['buildingType'];
        let buildingType = d.buildingTypes[buildingTypeName];
        let volume = m.payload['volume'] ? parseFloat(m.payload['volume']) : parseFloat(m.payload['weight']) / 2300;
        if (m.payload['prediction'].includes("Coulage")) {
          d.concretingVolume! += volume;
          d.concreting!++;
          if (buildingType)
            d.buildingTypes[buildingTypeName].volume += volume;
        }
        let value;
        if (buildingType && buildingType.variable) {
          value = m.payload[buildingType.variable];
          d.buildingTypes[buildingTypeName].value += parseFloat(value || 0);
        } else if (buildingType) {
          d.buildingTypes[buildingTypeName].value++;
        }
        d.volume += volume;


      })
      d.volumeText = d.volume.toFixed(2);
      d.linearText = d.linear.toFixed(2);
      d.surfaceText = d.surface.toFixed(2);
      d.concretingVolumeText = d.concretingVolume.toFixed(2);

      d.buildingTypeArray = Object.keys(d.buildingTypes)
        .map(k => ({
          name: k,
          icon: BUILDING_TYPE_CONFIG[k]?.icon || "info",
          unit: d.buildingTypes[k].unit,
          volume: d.buildingTypes[k].volume.toFixed(2),
          value: d.buildingTypes[k].value,
          valueText: d.buildingTypes[k].value.toFixed(2)
        }))
        .filter(b => b.value > 0)
    })
  }

  onLoaded3D() {
    this.selectedFragments.set(this.fragments)
  }

  setForgePoint(point: any) {
    let pointObj = new PointShape(point);
    this.viewer.impl.addOverlay("point", pointObj.mesh);
    this.viewer.points.push(pointObj)
  }


  setForgeZone(zone: any) {
    let zoneObj = new ZoneShape(zone);

    if (zone.geometry && zone.geometry.position && zone.geometry.position.length > 2) {
      zoneObj.drawGeometry(true);
    } else if (zone.polygon && zone.polygon.length > 2) {
      zoneObj.draw(true);
    }

    this.viewer.zones.push(zoneObj)
  }

  updateForge() {
    let pointList = [];
    let options = this.widgetDesign.forge;
    let model = this.model()

    if (!this.viewer || !model) return;

    if (!this.viewer.zones)
      this.viewer.zones = [];

    this.viewer.zones.forEach((z: { removeGeom: () => void; remove: () => void; }) => {
      z.removeGeom();
      z.remove();
    })

    if (!this.viewer.points)
      this.viewer.points = [];

    this.viewer.points.forEach((p: { mesh: { visible: boolean; }; }) => {
      if (p.mesh) {
        p.mesh.visible = false;
        this.viewer.impl.removeOverlay("point", p.mesh);
      }
    })

    this.viewer.impl.invalidate(true, true, true);

    if (options.showZones && this.widget.content.zones) {
      this.widget.content.zones.forEach((z: any) => {
        z.viewer = this.viewer;
        z.model = model;
        this.setForgeZone(z)
      })
    }

    if (this.widgetData)
      (this.widgetData as Array<{ x: number, y: number, z?: number, s?: string, scale?: number }>).forEach((d, i) => {
        if (d.x && d.y && d.x > -180 && d.x < 180) {

          if (Array.isArray(d.y)) d.y = d.y[0];
          if (isNaN(d.y) || d.y < -90 || d.y > 90) return;

          let { x, y, z } = threeDtools.applyTransform2({ x: d.x, y: d.y, z: d.z ?? 0, lon: undefined, lat: undefined }, model.origin, model.offset);
          let point: any = {};
          point.x = x;
          point.y = y;
          point.z = z;
          point.scale = model.origin.scale;
          this.setForgePoint(point);

          pointList.push({ lon: d.x, lat: d.y, z: d.z || model.origin.position.z, scale: d.scale || 1, color: d.s });
        }

        return;
      });

    this.viewer.impl.invalidate(true, true, true);
    this.viewer.impl.sceneUpdated(true);
  }

  onTasksSelected(taskIds: string[]) {
    if (!taskIds?.length) return;

    this.planningStore.setSelectedDay('');
    this.initPlanning3D(taskIds);
  }

  get selectedTasksLabel(): string {
    const count = this.tasksControl.value?.length || 0;

    if (count === 0) return '';
    if (count === 1) {
      const taskId = this.tasksControl.value?.[0] ?? '';
      const task = this.allTasks.find(t => t.id === taskId);

      return task ? `${task.number ?? ''} ${task.text ?? ''}`.trim() : taskId;
    }

    return `${count} tâches sélectionnées`;
  }

  trackTask(index: number, task: PlanningTask) {
    return task.id;
  }
}
